import React, { useEffect, useState } from "react";
import Axios from "axios";
import ReactGA from "react-ga4";
import LandingPageLayoutOne from "./landingPageLayoutOne";
import LandingPageLayoutTwo from "./landingPageLayoutTwo";
import { isBrowserSessionDemo, isDemoMode } from "../../utils/demoConfig";
import { getMockResponse } from "../../utils/mockApi";
import mockLinkLandingPagesData from "../../data/mockData/linkLandingPagesData";
import premadeColorsData from "../../data/mockData/premadeColorsData";
import { getDemoLandingRows } from "../../utils/demoLinkLandingSessionStore";
import {
	isWlDemoBlobRef,
	wlDemoBlobStorageKeyFromRef,
	getDemoTrackBlob,
} from "../../utils/demoTrackBlobStore";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;

function LinkLandingPage() {

	const [landingPageData, setLandingPageData] = useState(null);

	// Initialize Google Analytics
	useEffect(() => {
		ReactGA.initialize([
			{
				trackingId: GA_TRACKING_ID,
				testMode: true,
			},
		])
	}, [])

	useEffect(() => {
		getLinkInBioLink()
	}, [])

	const resolveBlobRefToObjectUrl = async (value) => {
		if (!isWlDemoBlobRef(value)) {
			return value;
		}
		try {
			const storageKey = wlDemoBlobStorageKeyFromRef(value);
			const blob = await getDemoTrackBlob(storageKey);
			return blob ? URL.createObjectURL(blob) : value;
		} catch {
			return value;
		}
	};

	const resolveAsset = async (asset) => {
		if (!asset) {
			return asset;
		}
		if (typeof asset === "string") {
			const resolved = await resolveBlobRefToObjectUrl(asset);
			return { thumb_small: resolved, full_size: resolved };
		}
		const thumb = await resolveBlobRefToObjectUrl(asset.thumb_small || asset.full_size);
		const full = await resolveBlobRefToObjectUrl(asset.full_size || asset.thumb_small);
		return { ...asset, thumb_small: thumb, full_size: full };
	};

	const resolveLandingPageAssets = async (data) => {
		if (!data || typeof data !== "object") {
			return data;
		}
		const customColor =
			data.custom_color && typeof data.custom_color === "object" ? data.custom_color : null;
		const premadeColor = premadeColorsData.find(
			item => String(item.id) === String(data.premade_color),
		);
		const selectedColor =
			Number(data.color_scheme) === 2 && customColor ? customColor : (premadeColor || null);
		const logo = await resolveAsset(data.logo);
		const backgroundImage = await resolveAsset(data.background_image);
		return {
			...data,
			layout: data.layout || 1,
			font: data.font || 26,
			button_corners: data.button_corners || 1,
			button_style: data.button_style || 1,
			social_icon_placement: data.social_icon_placement || 1,
			social_icon_color: data.social_icon_color || 1,
			color_scheme: data.color_scheme || 1,
			header_color_scheme: selectedColor?.header_color_scheme || data.header_color_scheme || 1,
			body_color_scheme: selectedColor?.body_color_scheme || data.body_color_scheme || 1,
			header_gradient: selectedColor?.header_gradient || data.header_gradient || null,
			body_gradient: selectedColor?.body_gradient || data.body_gradient || null,
			header_color: selectedColor?.header_color || data.header_color || "#ffffff",
			body_color: selectedColor?.body_color || data.body_color || "#ffffff",
			title_color: selectedColor?.title_color || data.title_color || "#000000",
			description_color: selectedColor?.description_color || data.description_color || "#000000",
			button_color: selectedColor?.button_color || data.button_color || "#111111",
			button_text_color: selectedColor?.button_text_color || data.button_text_color || "#ffffff",
			background_color: selectedColor?.background_color || data.background_color || "#ffffff",
			social_links: Array.isArray(data.social_links) ? data.social_links : [],
			featured_links: Array.isArray(data.featured_links) ? data.featured_links : [],
			logo,
			background_image: backgroundImage,
		};
	};

	const getLocalLandingByBackUrl = (backUrl) => {
		const normalized = String(backUrl || "").trim().toLowerCase();
		if (!normalized) {
			return null;
		}
		const map = new Map();
		(mockLinkLandingPagesData || []).forEach(row => {
			if (row && row.id != null) {
				map.set(String(row.id), row);
			}
		});
		(getDemoLandingRows() || []).forEach(row => {
			if (!row || row.id == null) {
				return;
			}
			const sid = String(row.id);
			const base = map.get(sid);
			if (!base) {
				map.set(sid, row);
				return;
			}
			const merged = { ...base, ...row };
			if (
				Array.isArray(base.social_links) &&
				base.social_links.length > 0 &&
				(!Array.isArray(row.social_links) || row.social_links.length === 0)
			) {
				merged.social_links = base.social_links;
			}
			if (
				Array.isArray(base.featured_links) &&
				base.featured_links.length > 0 &&
				(!Array.isArray(row.featured_links) || row.featured_links.length === 0)
			) {
				merged.featured_links = base.featured_links;
			}
			map.set(sid, merged);
		});
		const mergedRows = Array.from(map.values());
		const byBackUrl = mergedRows.find(
			row => String(row?.back_url || "").trim().toLowerCase() === normalized,
		);
		if (byBackUrl) {
			return byBackUrl;
		}

		return (
			mergedRows.find(row => {
				const fullUrl = String(row?.full_url || "").trim();
				if (!fullUrl) {
					return false;
				}
				try {
					const parsed = new URL(fullUrl, window.location.origin);
					const parts = parsed.pathname.split("/").filter(Boolean);
					const maybeBackUrl = parts[0] === "b" ? parts[1] : parts[0];
					return String(maybeBackUrl || "").trim().toLowerCase() === normalized;
				} catch {
					return false;
				}
			}) || null
		);
	};

	const getLinkInBioLink = async () => {
		const segments = window.location.pathname.split("/").filter(Boolean);
		const back_url = segments[0] === "b" ? segments[1] : segments[0];
		let domain = window.location.origin;
		domain = domain.replace(/^https?:\/\/(?:www\.)?/, '');

		const shouldPreferLocal =
			isDemoMode() || isBrowserSessionDemo() || window.location.hostname === "localhost";

		if (shouldPreferLocal) {
			const localRow = getLocalLandingByBackUrl(back_url);
			if (localRow) {
				const resolvedLocal = await resolveLandingPageAssets(localRow);
				setLandingPageData(resolvedLocal);
				ReactGA.send({ hitType: "pageview", page: window.location.href });
				return;
			}
		}

		if (isDemoMode() || isBrowserSessionDemo()) {
			const mockData = getMockResponse(`link-landingpage/?back_url=${back_url}&domain=${domain}`);
			if (!mockData || mockData.detail) {
				window.location.assign('/bioNotFound');
				return;
			}
			const resolvedData = await resolveLandingPageAssets(mockData);
			setLandingPageData(resolvedData);
			ReactGA.send({ hitType: "pageview", page: window.location.href });
			return;
		}

		try 
		{
			const result = await Axios.get(
				`${API_URL}link-landingpage/?back_url=${back_url}&domain=${domain}`
			);

			if (result.status === 200 && !result?.data?.detail) {
				const resolvedData = await resolveLandingPageAssets(result.data);
				setLandingPageData(resolvedData);
				ReactGA.send({ hitType: "pageview", page: window.location.href });
			} else {
				window.location.assign('/bioNotFound');
			}
		}
		catch (error) {
			window.location.assign('/bioNotFound');
		} 
	}

	return (
		<>
			{
				(landingPageData && landingPageData.layout === 1) && <LandingPageLayoutOne landingPageData={landingPageData} />
			}
			{
				(landingPageData && landingPageData.layout === 2) && <LandingPageLayoutTwo landingPageData={landingPageData} />
			}
		</>
	)
}

export default LinkLandingPage;
